import { useState, useMemo, useDeferredValue, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../ui/table";
import { Search, User, Calendar, ChevronDown } from "lucide-react";
import { Badge } from "../../ui/badge";
import { Device } from "../interfaces/EndpointInterfaces";
import { useToast } from "../../../hooks/use-toast";
import { myLocalStorage } from "../../StorageHelper";
import { SshUser } from "./sshuserinterface";
import { fetchSSHUsers, publishSSHUsers } from "./Api/ssh-userapi";
import PublishComponent from "../PublishComponent";
import { Skeleton } from "../../ui/skeleton";
import { getRolesList } from "../Endpoints/Api/EndpointApi";
import { Role } from "../interfaces/CommonInterfaces";
import { IoMdReturnLeft } from "react-icons/io";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../ui/tooltip";
import { formatDate } from "../formatTimeAgo";

interface SSHUsersTabProps {
    device: Device;
}

export default function SSHUsersTab({ device }: SSHUsersTabProps) {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [sshUsers, setSSHUsers] = useState<SshUser[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [roles, setRoles] = useState<Role[]>([]);
    const [loadingRoles, setLoadingRoles] = useState(true);

    const { toast } = useToast();
    const tenantName = myLocalStorage.getItem("latestTenant").tenantName || "";

    const itemsPerPage = 10;

    const deferredSearchTerm = useDeferredValue(searchTerm);

    const getSSHUsers = async (device: Device) => {
        setIsLoading(true);
        try {
            const response: SshUser[] = await fetchSSHUsers(tenantName, device.id);
            const filteredResponse = (response ?? []).filter(
                (user) => user.cn !== "root" && user.cn !== "nobody"
            );
            setSSHUsers(filteredResponse ?? []);
        } catch (error) {
            console.error("Error fetching SSH users:", error);
            setSSHUsers([]);
        } finally {
            setIsLoading(false);
        }
    };
    const getRoles = async () => {
        setLoadingRoles(true);
        try {
            const response = await getRolesList(tenantName);
            if (response && response.length > 0) {
                const formattedRoles: Role[] = response
                    .map((role: { id: number; roleName: string }) => ({
                        id: String(role.id),
                        name: role.roleName,
                    }))
                    .filter(
                        (role: Role) =>
                            role.name !== "epam-user" &&
                            role.name !== "super-user" &&
                            role.name !== "unassigned-user"
                    );

                setRoles(formattedRoles);
            } else {
                setRoles([]);
            }
        } finally {
            setLoadingRoles(false);
        }
    };

    useEffect(() => {
        getSSHUsers(device);
        getRoles();
    }, [device]);

    const filteredUsers = useMemo(() => {
        return sshUsers.filter((user) =>
            user.cn.toLowerCase().includes(deferredSearchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(deferredSearchTerm.toLowerCase())
        );
    }, [sshUsers, deferredSearchTerm]);

    // ✅ Pagination fix (based on filtered users)
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const paginatedUsers = filteredUsers.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [deferredSearchTerm]);

    const handlePublishRoles = async (userId: string, roles: string[], device: Device) => {

        if (!tenantName || !userId || !device) return;

        let response = await publishSSHUsers(tenantName, userId, roles, device?.id);
        if (response) {
            toast({
                title: "SSH User Roles Updated",
                description: roles.length > 0 ? `Successfully updated roles for SSH user` : "Unpublished from all roles",
            });
            getSSHUsers(device);
        }


    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case "active":
                return (
                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                        Active
                    </Badge>
                );
            case "inactive":
                return (
                    <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
                        Inactive
                    </Badge>
                );
            case "locked":
                return (
                    <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
                        Locked
                    </Badge>
                );
            default:
                return <Badge>{status}</Badge>;
        }
    };

    return (
        <div className="space-y-6 p-6" data-testid="ssh-users-tab">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-2xl font-semibold text-foreground">SSH Users</h2>
                <p className="text-sm text-muted-foreground mt-1">
                    Manage SSH user access and role assignments for {device.name}
                </p>
            </div>

            {/* Search */}
            <Card>
                <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search SSH users..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        SSH Users ({filteredUsers.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-6 space-y-4">
                            {Array.from({ length: 6 }).map((_, idx) => (
                                <div key={idx} className="flex items-center justify-between">
                                    <Skeleton className="w-1/5 h-5" />
                                    <Skeleton className="w-1/6 h-5" />
                                    <Skeleton className="w-1/5 h-5" />
                                    <Skeleton className="w-1/5 h-5" />
                                    <Skeleton className="w-1/6 h-5" />
                                </div>
                            ))}
                        </div>
                    ) : paginatedUsers.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="pl-6">Username</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Created Date</TableHead>
                                    <TableHead>Role Management</TableHead>
                                    <TableHead className="pr-6">Current Roles</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {paginatedUsers.map((user) => (
                                    <TableRow key={user.id}>
                                        <TableCell className="pl-6">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                                                    <User className="w-4 h-4 text-primary" />
                                                </div>
                                                <div>
                                                    <div className="font-medium">{user.cn}</div>

                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell><div className="font-medium">{user.email}</div></TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2 text-sm">
                                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                                <span>{formatDate(user.createdAt)}</span>
                                            </div>
                                        </TableCell>

                                        <TableCell>
                                            <PublishComponent
                                                entityName={user.username}
                                                entityType="SSH user"
                                                availableRoles={roles}
                                                publishedRoles={user.publishedRoles.map(role => role.roleId)}
                                                onPublish={(selectedRoles) =>
                                                    handlePublishRoles(String(user.id), selectedRoles, device)
                                                }
                                                disabled={false}
                                                buttonVariant="outline"
                                                buttonSize="sm"
                                            />
                                        </TableCell>
                                        <TableCell className="pr-6">
                                            <div className="flex flex-wrap gap-1">
                                                {user.publishedRoles?.length > 0 ? (
                                                    <>
                                                        {user.publishedRoles.slice(0, 2).map((role, index) => (
                                                            <Badge key={index} variant="secondary" className="text-xs">
                                                                {role.roleName}
                                                            </Badge>
                                                        ))}

                                                        {user.publishedRoles.length > 2 && (
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <button
                                                                            className="text-primary hover:text-primary/80 transition-colors cursor-pointer inline-flex items-center gap-0.5"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            <span className="font-medium">
                                                                                +{user.publishedRoles.length - 2} more
                                                                            </span>
                                                                            <ChevronDown className="w-3 h-3" />
                                                                        </button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent side="top" className="max-w-sm p-3">
                                                                        <div className="text-sm">
                                                                            <div className="mb-2 font-semibold text-foreground">
                                                                                All Published Roles ({user.publishedRoles.length})
                                                                            </div>
                                                                            <div className="space-y-1">
                                                                                {user.publishedRoles.slice(2).map((role, index) => (
                                                                                    <div key={index} className="text-muted-foreground">
                                                                                        • {role.roleName}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="text-sm text-muted-foreground italic">
                                                        No roles assigned
                                                    </span>
                                                )}
                                            </div>

                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <div className="p-8 text-center">
                            <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-foreground mb-2">
                                No SSH Users Found
                            </h3>
                            <p className="text-muted-foreground">
                                {searchTerm
                                    ? "No SSH users match your search criteria."
                                    : "No SSH users configured for this device."}
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Pagination */}
            {!isLoading && totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                        Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                        {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of{" "}
                        {filteredUsers.length} SSH users
                    </div>
                    <div className="flex items-center space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </Button>
                        <span className="text-sm font-medium">
                            Page {currentPage} of {totalPages}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                            }
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
